import { DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { Subject, takeUntil } from 'rxjs';
import { GeneralService } from 'src/app/services/general.service';
import { VendorService } from 'src/app/services/Vendor.service';
import { WebInquirySettingsService } from 'src/app/services/WebInquirySettings.service';
import { PhoneFormatPipe } from 'src/app/Shared/phone-format.pipe';
import { ANGULAR_MODULES, SYNCFUSION_MODULES } from 'src/app/Shared/shared-imports';
import { SharedHTMLSettingsDataService } from 'src/app/Shared/sharedHTMLSettingsData.service';
import { WordReplacePipe } from 'src/app/Shared/word-replace.pipe';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-ops-web-inquiry-settings',
  imports: [ANGULAR_MODULES, SYNCFUSION_MODULES, WordReplacePipe, PhoneFormatPipe],
  providers: [DatePipe],
  templateUrl: './ops-web-inquiry-settings.html',
  styleUrl: './ops-web-inquiry-settings.scss'
})

export class OpsWebInquirySettings implements OnInit, OnDestroy {

  private destroy$ = new Subject<void>();
  @Output() close = new EventEmitter<void>();
  @Input() vendorId!: number;
  vendorInfo: any;
  vendorMarketID = '';
  vendorName = '';
  telephone = '';
  Email = '';
  address = '';
  webInquiryForm!: FormGroup;
  hintQuestions = [
    { Id: 1, Name: 'What is your favorite color?' },
    { Id: 2, Name: 'What was the name of your first school?' },
    { Id: 3, Name: 'What is your favorite food?' }
  ];
  marketCurrentDate!: Date;
  showPassword = false;

  constructor(
    private toastr: ToastrService,
    private vendorService: VendorService,
    private generalService: GeneralService,
    private webInquiryService: WebInquirySettingsService,
    private fb: FormBuilder,
    private datePipe: DatePipe,
    private sharedHTMLSettings: SharedHTMLSettingsDataService,
  ) { }

  ngOnInit(): void {
    this.loadMarketCurrentDate();
    this.initForm();
    this.getVendorInfo();
    setTimeout(() => { this.loadWebInquiryUser(); }, 300);
  }

  initForm(): void {
    this.webInquiryForm = this.fb.group({
      UserId: [0],
      EmailId: [{ value: '', disabled: true }, [Validators.required]],
      Password: ['', [Validators.required,]],
      VendorId: [],
      SecretQuestionId: [null],
      SecretAnswer: [''],
      AllowOnlineAccess: [false],
      RegDate: [null]   //yyyy-MM-dd
    });

  }

  loadMarketCurrentDate(): void {
    this.generalService.GetMarketCurrentTime().pipe(takeUntil(this.destroy$)).subscribe({
      next: (marketTime: string) => {
        const marketDate = marketTime.split('T')[0];
        this.marketCurrentDate = this.toLocalDate(marketDate);
      },
      error: (err) => {
        console.error("Error fetching market time", err);
      }
    });
  }

  toLocalDate(dateString: string): Date {
    const [y, M, d] = dateString.split('-').map(Number);
    return new Date(y, M - 1, d);
  }

  getVendorInfo() {
    this.vendorService.GetVendorById(this.vendorId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.vendorInfo = res;
        this.vendorMarketID = res.MarketVendorId;
        this.vendorName = res.VendorName;
        this.telephone = res.Telephone;
        this.Email = res.Email;
        this.address = res.Address;

        if (res.Email) {
          this.webInquiryForm.patchValue({
            EmailId: res.Email
          });
        }

      },
      error: (err) => console.error('Failed to load vendor info', err)
    });
  }

  loadWebInquiryUser() {
    this.webInquiryService.getWebInquiryUser(this.vendorId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.webInquiryForm.patchValue({
          UserId: res.UserId,
          // EmailId: res.EmailId,
          Password: res.Password,
          VendorId: res.VendorId,
          SecretQuestionId: res.SecretQuestionId,
          SecretAnswer: res.SecretAnswer,
          AllowOnlineAccess: res.AllowOnlineAccess,
          RegDate: res.RegDate
        });

        const regDateCtrl = this.webInquiryForm.get('RegDate');
        const currentRegDate = regDateCtrl?.value;
        if (!res.UserId) {
          regDateCtrl?.enable();
          regDateCtrl?.setValue(this.datePipe.transform(this.marketCurrentDate, "yyyy-MM-dd"));
        } else {
          if (!currentRegDate) {
            regDateCtrl?.setValue(this.datePipe.transform(this.marketCurrentDate, "yyyy-MM-dd"));
          }
          regDateCtrl?.disable();
        }

      },
      error: (err) =>
        console.error('Failed to load Web Inquiry User', err)
    });
  }

  saveWebInquiryUser(sendEmail: boolean = false): void {
    if (this.webInquiryForm.invalid) {
      this.webInquiryForm.markAllAsTouched();
      this.toastr.error('Please fill all required fields', $localize`Error`, this.sharedHTMLSettings.ToasterConfig);
      return;
    }

    const formValue = this.webInquiryForm.getRawValue();
    const payload = {
      UserId: formValue.UserId,
      EmailId: formValue.EmailId?.trim(),
      Password: formValue.Password?.trim(),
      VendorId: this.vendorId,
      SecretQuestionId: formValue.SecretQuestionId,
      SecretAnswer: formValue.SecretAnswer?.trim(),
      AllowOnlineAccess: formValue.AllowOnlineAccess,
      RegDate: formValue.RegDate
    };

    const swalTitle = sendEmail ? 'Confirm Save & Email' : 'Confirm Save';
    const swalText = sendEmail
      ? 'Are you sure you want to save the login details and send the Web Inquiry login credentials email?'
      : 'Are you sure you want to save the Web Inquiry login details?';

    Swal.fire({
      title: swalTitle,
      text: swalText,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        this.webInquiryService.saveWebInquiryUser(payload).pipe(takeUntil(this.destroy$)).subscribe({
          next: (res) => {
            if (res?.Success) {
              this.toastr.success(res.Message || 'Saved successfully', $localize`Success`, this.sharedHTMLSettings.ToasterConfig);
              if (sendEmail) {
                this.sendEmail(false);
              }
            } else {
              this.toastr.error(res?.Message || 'Save failed', $localize`Error`, this.sharedHTMLSettings.ToasterConfig);
            }
          },
          error: (err) => {
            console.error('Save failed', err);
            this.toastr.error('Failed to save');
          }
        });
      }
    });
  }

  sendEmail(showConfirm: boolean = true): void {

    if (!showConfirm) {
      this.callSendEmailApi();
      return;
    }

    Swal.fire({
      title: 'Confirmation',
      text: 'Are you sure you want to send the Web Inquiry login credentials email?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, Send',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        this.callSendEmailApi();
      }
    });
  }

  private callSendEmailApi(): void {
    this.webInquiryService.webInquiryLoginDetailsEmail(this.vendorId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res?.Success) {
            this.toastr.success(
              res?.message || 'Web Inquiry login credentials email sent successfully.',
              $localize`Success`,
              this.sharedHTMLSettings.ToasterConfig
            );
          } else {
            this.toastr.error(
              res?.message || 'Failed to send Web Inquiry login credentials email.',
              $localize`Error`,
              this.sharedHTMLSettings.ToasterConfig
            );
          }
        },
        error: () => {
          this.toastr.error(
            'An error occurred while sending the email. Please try again.',
            $localize`Error`,
            this.sharedHTMLSettings.ToasterConfig
          );
        }
      });
  }

  goBack() {
    this.close.emit();
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
